<script setup lang="ts">
import { createPageAccessToken } from '~/api/wikitrade-market'
import { request } from '~/api/request'

type MarketTickerItem = {
  id: string
  symbol: string
  price: string
  change: string
  trend: 'up' | 'down'
  icon: string
}

type MarketTickerResponse = {
  simulationtradetemp?: {
    symbolcategorymarketlist?: {
      data?: {
        list?: MarketTickerItem[]
      }
    }
  }
}

const MARKET_LIST_ROUTE = 'simulationtradetemp$symbolcategorymarketlist'
const WIKITRADE_BASIC_DATA = '999,0,100,0,0,822ca7b3261a0b2ec10c8cdabfb5a0ea,0'
const api = useEnvApi()
const { localePath } = useAppI18n()
const matomo = useMatomo()

const { data: marketList } = await useAsyncData<MarketTickerItem[]>(
  'home-market-ticker',
  async () => {
    if (import.meta.client) return []

    try {
      const accessToken = await createPageAccessToken(api.wikitrade)
      const response = await request<MarketTickerResponse>({
        baseURL: api.wikitrade,
        url: '/forexpayweb-v1/invoke-v3/tradequote',
        method: 'POST',
        data: {},
        headers: {
          Authorization: `Bearer ${accessToken}`,
          basicdata1: WIKITRADE_BASIC_DATA,
          PreferredLanguageCode: 'zh-cn',
          Routes: MARKET_LIST_ROUTE,
          TradeToken: '',
          'Web-Version': '5',
          WikiBrokerCode: '1627938149',
        },
      })
      return response?.simulationtradetemp?.symbolcategorymarketlist?.data?.list || []
    } catch {
      return []
    }
  },
  {
    default: () => [],
  },
)

const tickerItems = computed(() => marketList.value || [])
const tickerDuration = computed(() => Math.max(40, tickerItems.value.length * 4))
const getMarketLink = (item: MarketTickerItem) => ({
  path: localePath('/market'),
  query: { code: item.id },
})
const goMarketPage = (item: MarketTickerItem) => {
  matomo.trackEvent('home_market', {
    af_content: item.symbol,
    af_code: item.id,
  })
}
</script>

<template>
  <section
    v-if="tickerItems.length"
    class="home-market-ticker fixed right-[0px] bottom-[0px] left-[0px] z-[30] w-full border-t border-solid border-t-[rgba(5,21,36,0.06)] bg-white"
  >
    <div
      class="market-ticker-scroll flex min-h-[34px] w-full items-center overflow-hidden px-[24px] py-[7px] font-[Outfit,sans-serif] text-[14px] font-normal text-[#292a2e]"
    >
      <div
        class="market-ticker-track flex w-max items-center"
        :style="{ '--market-ticker-duration': `${tickerDuration}s` }"
      >
        <div
          v-for="groupIndex in 2"
          :key="groupIndex"
          class="flex shrink-0 items-center gap-[40px] pr-[40px]"
          :aria-hidden="groupIndex === 2"
        >
          <NuxtLink
            v-for="item in tickerItems"
            :key="`${groupIndex}-${item.id}-${item.symbol}`"
            :to="getMarketLink(item)"
            @click="goMarketPage(item)"
            :tabindex="groupIndex === 2 ? -1 : undefined"
            target="_blank"
            class="flex shrink-0 items-center gap-[8px] leading-none whitespace-nowrap text-[#292a2e] no-underline"
          >
            <CommonImage
              :src="item.icon"
              :alt="item.symbol"
              class="h-[18px] w-[18px] shrink-0 rounded-full object-cover"
              loading="eager"
            />
            <span>{{ item.symbol }}</span>
            <span>{{ item.price }}</span>
            <span
              class="flex items-center gap-[2px]"
              :class="[item.trend === 'up' ? 'text-[#22a06b]' : '', item.trend === 'down' ? 'text-[#e2483d]' : '']"
            >
              <span>{{ item.change }}</span>
              <span :class="item.trend === 'down' ? 'text-[#e2483d]' : ''">
                {{ item.trend === 'up' ? '↑' : '↓' }}
              </span>
            </span>
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="less">
.market-ticker-track {
  animation: marketTickerMarquee var(--market-ticker-duration) linear infinite;
  will-change: transform;
}

.market-ticker-scroll:hover {
  .market-ticker-track {
    animation-play-state: paused;
  }
}

@keyframes marketTickerMarquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(-50%);
  }
}
</style>
